/**
 * HolidayEffects — full-screen ambient overlay rendered from a theme's
 * `effects` flags. Pure CSS keyframe animations; transforms only; no JS
 * timers, no canvas — GPU-accelerated so it costs ~0% CPU at idle.
 *
 * Respects `prefers-reduced-motion`. Pointer-events: none. z-index sits
 * above the storefront grid but below modals (PDP / cart drawer).
 *
 * Supported flags (see hp_themes.py SEED_THEMES.effects):
 *   snowfall      — 36 drifting snowflakes (Christmas)
 *   confetti      — 60 falling colored pieces (New Year / Australia Day)
 *   sparkle       — twinkling stars at random points
 *   hearts        — floating pink hearts (Valentine's)
 *   petals        — drifting flower petals (Mother's Day / Easter)
 *   ghosts        — soft glowing ghosts drifting across (Halloween)
 *   lightning     — brief screen flashes (Black Friday)
 *   embers        — slow rising orange embers (Father's Day)
 *   lanterns      — swaying red paper lanterns (Lunar New Year)
 *   diya          — glowing lights drifting up (Diwali)
 *   pride_stripes — rainbow stripe at top edge (Pride)
 */
import { useMemo } from "react";
import "./HolidayEffects.css";

// Helper to generate stable random offsets keyed off i — same on every
// re-render so we don't restart animations mid-cycle.
const seeded = (i, salt = 1) =>
  ((Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453) % 1 + 1) % 1;

function Snowflakes({ count = 36 }) {
  const flakes = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 1) * 100}%`,
    size:     6 + seeded(i, 2) * 12,
    delay:    -seeded(i, 3) * 10,
    duration: 8 + seeded(i, 4) * 10,
    drift:    (seeded(i, 5) - 0.5) * 80,
    opacity:  0.35 + seeded(i, 6) * 0.5,
  })), [count]);
  return (
    <div className="hpz-fx-snow" aria-hidden>
      {flakes.map((f, i) => (
        <span
          key={i}
          style={{
            left: f.left,
            width: `${f.size}px`,
            height: `${f.size}px`,
            opacity: f.opacity,
            animationDelay: `${f.delay}s`,
            animationDuration: `${f.duration}s`,
            "--drift": `${f.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function Confetti({ count = 60 }) {
  const palette = ["#f3c84b", "#ff5e8a", "#22c7de", "#5eff8a", "#ffd400", "#ff7518"];
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 11) * 100}%`,
    size:     5 + seeded(i, 12) * 7,
    color:    palette[Math.floor(seeded(i, 13) * palette.length)],
    delay:    -seeded(i, 14) * 8,
    duration: 5 + seeded(i, 15) * 7,
    spin:     (seeded(i, 16) - 0.5) * 720,
    drift:    (seeded(i, 17) - 0.5) * 200,
  })), [count]);
  return (
    <div className="hpz-fx-confetti" aria-hidden>
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: p.left,
            width:  `${p.size}px`,
            height: `${p.size * 1.6}px`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--spin":  `${p.spin}deg`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function Sparkles({ count = 24 }) {
  const stars = useMemo(() => Array.from({ length: count }, (_, i) => ({
    top:      `${seeded(i, 21) * 100}%`,
    left:     `${seeded(i, 22) * 100}%`,
    size:     3 + seeded(i, 23) * 5,
    delay:    -seeded(i, 24) * 5,
    duration: 2 + seeded(i, 25) * 3,
  })), [count]);
  return (
    <div className="hpz-fx-sparkle" aria-hidden>
      {stars.map((s, i) => (
        <span
          key={i}
          style={{
            top: s.top, left: s.left,
            width: `${s.size}px`, height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function Hearts({ count = 18 }) {
  const hearts = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 31) * 100}%`,
    size:     14 + seeded(i, 32) * 14,
    delay:    -seeded(i, 33) * 9,
    duration: 7 + seeded(i, 34) * 6,
    drift:    (seeded(i, 35) - 0.5) * 100,
    hue:      330 + seeded(i, 36) * 30,
  })), [count]);
  return (
    <div className="hpz-fx-hearts" aria-hidden>
      {hearts.map((h, i) => (
        <span
          key={i}
          style={{
            left: h.left,
            fontSize: `${h.size}px`,
            color: `hsl(${h.hue}, 80%, 65%)`,
            animationDelay: `${h.delay}s`,
            animationDuration: `${h.duration}s`,
            "--drift": `${h.drift}px`,
          }}
        >♥</span>
      ))}
    </div>
  );
}

function Petals({ count = 22 }) {
  const palette = ["#ffc0cb", "#ffe5d8", "#f9bcc5", "#fdf2c4", "#e8a5a0"];
  const petals = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 41) * 100}%`,
    size:     8 + seeded(i, 42) * 10,
    color:    palette[Math.floor(seeded(i, 43) * palette.length)],
    delay:    -seeded(i, 44) * 10,
    duration: 8 + seeded(i, 45) * 8,
    drift:    (seeded(i, 46) - 0.5) * 160,
    spin:     seeded(i, 47) * 360,
  })), [count]);
  return (
    <div className="hpz-fx-petals" aria-hidden>
      {petals.map((p, i) => (
        <span
          key={i}
          style={{
            left: p.left,
            width:  `${p.size}px`,
            height: `${p.size * 0.7}px`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--spin": `${p.spin}deg`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function Ghosts({ count = 5 }) {
  const ghosts = useMemo(() => Array.from({ length: count }, (_, i) => ({
    top:      `${10 + seeded(i, 51) * 70}%`,
    size:     30 + seeded(i, 52) * 30,
    delay:    -seeded(i, 53) * 18,
    duration: 18 + seeded(i, 54) * 10,
    opacity:  0.18 + seeded(i, 55) * 0.20,
  })), [count]);
  return (
    <div className="hpz-fx-ghosts" aria-hidden>
      {ghosts.map((g, i) => (
        <span
          key={i}
          style={{
            top: g.top,
            fontSize: `${g.size}px`,
            opacity: g.opacity,
            animationDelay: `${g.delay}s`,
            animationDuration: `${g.duration}s`,
          }}
        >👻</span>
      ))}
    </div>
  );
}

function Lightning() {
  return <div className="hpz-fx-lightning" aria-hidden />;
}

function Embers({ count = 24 }) {
  const sparks = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 61) * 100}%`,
    size:     2 + seeded(i, 62) * 4,
    delay:    -seeded(i, 63) * 7,
    duration: 6 + seeded(i, 64) * 5,
    drift:    (seeded(i, 65) - 0.5) * 50,
  })), [count]);
  return (
    <div className="hpz-fx-embers" aria-hidden>
      {sparks.map((s, i) => (
        <span
          key={i}
          style={{
            left: s.left,
            width: `${s.size}px`, height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
            "--drift": `${s.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function Lanterns({ count = 6 }) {
  const lanterns = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${5 + seeded(i, 71) * 90}%`,
    top:      `${seeded(i, 72) * 25}%`,
    size:     34 + seeded(i, 73) * 14,
    delay:    -seeded(i, 74) * 6,
    duration: 5 + seeded(i, 75) * 4,
  })), [count]);
  return (
    <div className="hpz-fx-lanterns" aria-hidden>
      {lanterns.map((l, i) => (
        <span
          key={i}
          style={{
            left: l.left, top: l.top,
            fontSize: `${l.size}px`,
            animationDelay: `${l.delay}s`,
            animationDuration: `${l.duration}s`,
          }}
        >🏮</span>
      ))}
    </div>
  );
}

function Diya({ count = 18 }) {
  const lights = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left:     `${seeded(i, 81) * 100}%`,
    size:     6 + seeded(i, 82) * 6,
    delay:    -seeded(i, 83) * 8,
    duration: 7 + seeded(i, 84) * 5,
    hue:      30 + seeded(i, 85) * 30,
    drift:    (seeded(i, 86) - 0.5) * 60,
  })), [count]);
  return (
    <div className="hpz-fx-diya" aria-hidden>
      {lights.map((d, i) => (
        <span
          key={i}
          style={{
            left: d.left,
            width: `${d.size}px`, height: `${d.size}px`,
            background: `radial-gradient(circle, hsl(${d.hue},100%,70%) 0%, transparent 70%)`,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
            "--drift": `${d.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

function PrideStripes() {
  return <div className="hpz-fx-pride" aria-hidden />;
}


export default function HolidayEffects({ theme }) {
  const fx = theme?.effects || {};
  if (!fx || Object.keys(fx).length === 0) return null;
  return (
    <div className="hpz-fx-root" data-testid="plaza-holiday-fx"
          data-theme-key={theme?.key}>
      {fx.snowfall      && <Snowflakes />}
      {fx.confetti      && <Confetti />}
      {fx.hearts        && <Hearts />}
      {fx.petals        && <Petals />}
      {fx.ghosts        && <Ghosts />}
      {fx.embers        && <Embers />}
      {fx.lanterns      && <Lanterns />}
      {fx.diya          && <Diya />}
      {fx.lightning     && <Lightning />}
      {fx.sparkle       && <Sparkles />}
      {fx.pride_stripes && <PrideStripes />}
    </div>
  );
}
